import { mobilePlotArea, mobileLayerStock } from '../../../utils/mobileCalculations';
import React from 'react';
import { calculateSKS, calculateOtpadObsh, calculateOtpadTek } from './utils';

const CalculationsSection = ({
  displayData,
  calculateTotalsByNasazhdeniya,
  calculationsScrollRef,
  calculationsScrollPosition,
  handleCalculationsSliderChange,
  handleCalculationsScroll,
  handbooks = [],
  savedModalData = {},
  independentForm2Data = null,
  form1Data = null,
  dataType = 'to'
}) => {
  // Функция для расчета итогов по ярусу
  const calculateYarusTotals = (yarusNumber, column) => {
    const yarusData = displayData.data.filter(item => {
      const itemYarus = String(item.yarus || '').trim();
      const targetYarus = String(yarusNumber || '').trim();
      const match = itemYarus === targetYarus || Number(itemYarus) === Number(targetYarus);
      return match;
    });
    
    if (yarusData.length === 0) {
      return 0;
    }
    
    const total = yarusData.reduce((sum, item, index) => {
      let value = 0;
      
      if (column === 'sksporoda') {
        value = calculateSKS(item);
      } else if (column === 'ooporoda') {
        value = calculateOtpadObsh(item);
      } else if (column === 'toporoda') {
        value = calculateOtpadTek(item);
      } else {
        value = Number(item[column]) || 0;
      }
      
      const dolap = Number(item.dolap) || 0;
      const contribution = value * dolap / 10;
      const newSum = sum + contribution;
      
      return newSum;
    }, 0);
    
    return total;
  };

  // Функция для рендеринга ячейки итога по ярусу
  const renderYarusTotalCell = (yarusNumber, column) => {
    const yarusData = displayData.data.filter(item => {
      const itemYarus = String(item.yarus || '').trim();
      const targetYarus = String(yarusNumber || '').trim();
      const match = itemYarus === targetYarus || Number(itemYarus) === Number(targetYarus);
      return match;
    });
    
    if (yarusData.length === 0) {
      return '0.00';
    }
    
    const total = yarusData.reduce((sum, item, index) => {
      let value = 0;
      
      if (column === 'sksporoda') {
        value = calculateSKS(item);
      } else if (column === 'ooporoda') {
        value = calculateOtpadObsh(item);
      } else if (column === 'toporoda') {
        value = calculateOtpadTek(item);
      } else {
        value = Number(item[column]) || 0;
      }
      
      const dolap = Number(item.dolap) || 0;
      const contribution = value * dolap / 10;
      const newSum = sum + contribution;
      
      return newSum;
    }, 0);
    
    const result = total.toFixed(2);
    
    return result;
  };

  const totals = calculateTotalsByNasazhdeniya();

  // П. 2.14: во всех случаях, кроме 100% здоровых насаждений и (или) здоровых
  // и ослабленных (categories I и I+II), предупредить о необходимости заполнения
  // причин и признаков повреждения — мягкое предупреждение, ничего не блокирует.
  const healthyPercent = parseFloat(totals.bpodolap) || 0;
  const healthyPlusWeakenedPercent = healthyPercent + (parseFloat(totals.osldolap) || 0);
  const isFullyHealthyOrWeakened = healthyPlusWeakenedPercent >= 99.995; // допуск на округление
  const hasCauseSignsFilled = (displayData?.data || []).some(item =>
    (item?.variant4?.damageSigns || []).some(ds => ds && ds.prichina)
  );
  const showCauseSignWarning = Object.keys(totals).length > 0 && !isFullyHealthyOrWeakened && !hasCauseSignsFilled;

  // Функция для расчета доли повреждённых деревьев по причинам для яруса
  const calculateCausesForYarus = (yarusNumber, useSpecificDola = false) => {
    if (!displayData || !displayData.data) {
      return '';
    }
    
    // Получаем все строки данного яруса
    const yarusRows = displayData.data.filter(item => {
      const itemYarus = String(item.yarus || '').trim();
      const targetYarus = String(yarusNumber || '').trim();
      return itemYarus === targetYarus || Number(itemYarus) === Number(targetYarus);
    });
    
    if (yarusRows.length === 0) {
      return '';
    }
    
    // Получаем справочник причин (ID 10) для получения code
    const causesHandbook = handbooks.find(h => h.id === 10);
    const signsHandbook = handbooks.find(h => h.id === 9);
    
    // Собираем все причины из всех строк яруса
    const causesMap = new Map(); // key: causeCode, value: { priznCodes: Set, resSum: number }
    
    yarusRows.forEach((row) => {
      const rowId = row.id;
      const dolap = row.dolap;
      
      // Если доля породы "+" или не число - рассматриваем как 0
      const dolapValue = (dolap === '+' || isNaN(parseFloat(dolap))) ? 0 : parseFloat(dolap) || 0;
      
      // Получаем данные модального окна для этой строки
      const modalDataForRow = savedModalData[rowId];
      if (!modalDataForRow || !Array.isArray(modalDataForRow) || modalDataForRow.length === 0) {
        return;
      }
      
      modalDataForRow.forEach((modalItem) => {
        if (!modalItem.prichina) {
          return;
        }
        
        // Выбираем долю в зависимости от параметра
        let dola = 0;
        if (useSpecificDola) {
          // Для правого столбца используем специальную долю, если её нет - 0
          dola = parseFloat(modalItem.specificDolaPrizn) || 0;
        } else {
          // Для левого столбца используем обычную долю
          if (!modalItem.dolaprizn) {
            return; // Пропускаем записи без доли
          }
          dola = parseFloat(modalItem.dolaprizn) || 0;
        }
        
        // Получаем code причины
        let causeCode = modalItem.prichina; // По умолчанию используем ID
        if (causesHandbook && causesHandbook.references) {
          const cause = causesHandbook.references.find(
            r => r.id.toString() === String(modalItem.prichina)
          );
          if (cause && cause.code) {
            causeCode = cause.code.toString();
          }
        }
        
        // Вычисляем РЕЗ = (dola * dolap) / 10
        const res = (dola * dolapValue) / 10;
        
        // Получаем code признаков (может быть несколько)
        const priznCodesSet = new Set();
        
        // Если есть prizn (одиночный признак)
        if (modalItem.prizn) {
          let priznCode = '';
          if (signsHandbook && signsHandbook.references) {
            const sign = signsHandbook.references.find(
              r => r.id.toString() === String(modalItem.prizn)
            );
            if (sign && sign.code) {
              priznCode = sign.code.toString();
            } else {
              priznCode = modalItem.prizn;
            }
          } else {
            priznCode = modalItem.prizn;
          }
          if (priznCode) {
            priznCodesSet.add(priznCode);
          }
        }
        
        // Если есть priznList (массив признаков)
        if (modalItem.priznList && Array.isArray(modalItem.priznList) && modalItem.priznList.length > 0) {
          modalItem.priznList.forEach(priznItem => {
            let code = '';
            if (typeof priznItem === 'string') {
              if (signsHandbook && signsHandbook.references) {
                const sign = signsHandbook.references.find(
                  r => r.id.toString() === String(priznItem)
                );
                code = sign && sign.code ? sign.code.toString() : priznItem;
              } else {
                code = priznItem;
              }
            } else if (priznItem && priznItem.code) {
              code = priznItem.code.toString();
            } else if (priznItem && priznItem.id) {
              if (signsHandbook && signsHandbook.references) {
                const sign = signsHandbook.references.find(
                  r => r.id.toString() === String(priznItem.id)
                );
                code = sign && sign.code ? sign.code.toString() : priznItem.id.toString();
              } else {
                code = priznItem.id.toString();
              }
            }
            if (code) {
              priznCodesSet.add(code);
            }
          });
        }
        
        // Добавляем или обновляем причину в карте
        if (!causesMap.has(causeCode)) {
          causesMap.set(causeCode, {
            priznCodes: new Set(),
            resSum: 0
          });
        }
        
        const causeData = causesMap.get(causeCode);
        // Добавляем все признаки в Set (без дубликатов)
        priznCodesSet.forEach(code => {
          causeData.priznCodes.add(code);
        });
        causeData.resSum += res;
      });
    });
    
    // Форматируем результат
    const causesList = [];
    causesMap.forEach((causeData, causeCode) => {
      const priznStr = causeData.priznCodes.size > 0 
        ? `(${Array.from(causeData.priznCodes).join(', ')})` 
        : '';
      const formattedCause = `${causeCode}${priznStr}/${causeData.resSum.toFixed(1)}`;
      causesList.push(formattedCause);
    });
    
    return causesList.join('; ');
  };

  // Функция для расчета obj (сумма zapassir + zapassuh + zapaszahl) для яруса
  const calculateObjForYarus = (yarusNumber) => {
    if (!independentForm2Data || !independentForm2Data.yarusy_dependent) {
      return 0;
    }
    
    // Фильтруем по ярусу
    const yarusRows = independentForm2Data.yarusy_dependent.filter(item => {
      const itemYarus = String(item.yarus || '').trim();
      const targetYarus = String(yarusNumber || '').trim();
      return itemYarus === targetYarus || Number(itemYarus) === Number(targetYarus);
    });
    
    if (yarusRows.length === 0) {
      return 0;
    }
    
    const area = mobilePlotArea(form1Data, dataType);
    const obj = yarusRows.reduce((sum, tier) => sum + mobileLayerStock(tier, area), 0);

    return obj;
  };

  // Функция для расчета доли повреждённых деревьев по причинам для итога по насаждению
  const calculateCausesForNasazhdeniya = (useSpecificDola = false) => {
    if (!displayData || !displayData.data) {
      return '';
    }
    
    // Рассчитываем obj для каждого яруса
    const obj1 = calculateObjForYarus(1);
    const obj2 = calculateObjForYarus(2);
    const obj3 = calculateObjForYarus(3);
    const totalObj = obj1 + obj2 + obj3;
    
    if (totalObj === 0) {
      return '';
    }
    
    // Получаем справочники
    const causesHandbook = handbooks.find(h => h.id === 10);
    const signsHandbook = handbooks.find(h => h.id === 9);
    
    // Собираем все причины из всех ярусов
    const causesMap = new Map(); // key: causeCode, value: { priznCodes: Set, resSum: number }
    
    // Обрабатываем каждый ярус
    [1, 2, 3].forEach(yarusNumber => {
      const objYarus = yarusNumber === 1 ? obj1 : (yarusNumber === 2 ? obj2 : obj3);
      
      // Получаем все строки данного яруса
      const yarusRows = displayData.data.filter(item => {
        const itemYarus = String(item.yarus || '').trim();
        const targetYarus = String(yarusNumber || '').trim();
        return itemYarus === targetYarus || Number(itemYarus) === Number(targetYarus);
      });
      
      if (yarusRows.length === 0) {
        return;
      }
      
      yarusRows.forEach((row) => {
        const rowId = row.id;
        const dolap = row.dolap;
        
        // Если доля породы "+" или не число - рассматриваем как 0
        const dolapValue = (dolap === '+' || isNaN(parseFloat(dolap))) ? 0 : parseFloat(dolap) || 0;
        
        // Получаем данные модального окна для этой строки
        const modalDataForRow = savedModalData[rowId];
        if (!modalDataForRow || !Array.isArray(modalDataForRow) || modalDataForRow.length === 0) {
          return;
        }
        
        modalDataForRow.forEach((modalItem) => {
          if (!modalItem.prichina) {
            return;
          }
          
          // Выбираем долю в зависимости от параметра
          let dola = 0;
          if (useSpecificDola) {
            // Для правого столбца используем специальную долю, если её нет - 0
            dola = parseFloat(modalItem.specificDolaPrizn) || 0;
          } else {
            // Для левого столбца используем обычную долю
            if (!modalItem.dolaprizn) {
              return; // Пропускаем записи без доли
            }
            dola = parseFloat(modalItem.dolaprizn) || 0;
          }
          
          // Получаем code причины
          let causeCode = modalItem.prichina; // По умолчанию используем ID
          if (causesHandbook && causesHandbook.references) {
            const cause = causesHandbook.references.find(
              r => r.id.toString() === String(modalItem.prichina)
            );
            if (cause && cause.code) {
              causeCode = cause.code.toString();
            }
          }
          
          // Вычисляем РЕЗ для яруса = (dola * dolap) / 10
          const resYarus = (dola * dolapValue) / 10;
          
          // Вычисляем итоговое значение: (РЕЗ_яруса * obj_яруса) / (obj_яруса1 + obj_яруса2 + obj_яруса3)
          const resTotal = (resYarus * objYarus) / totalObj;
          
          // Получаем code признаков (может быть несколько)
          const priznCodesSet = new Set();
          
          // Если есть prizn (одиночный признак)
          if (modalItem.prizn) {
            let priznCode = '';
            if (signsHandbook && signsHandbook.references) {
              const sign = signsHandbook.references.find(
                r => r.id.toString() === String(modalItem.prizn)
              );
              if (sign && sign.code) {
                priznCode = sign.code.toString();
              } else {
                priznCode = modalItem.prizn;
              }
            } else {
              priznCode = modalItem.prizn;
            }
            if (priznCode) {
              priznCodesSet.add(priznCode);
            }
          }
          
          // Если есть priznList (массив признаков)
          if (modalItem.priznList && Array.isArray(modalItem.priznList) && modalItem.priznList.length > 0) {
            modalItem.priznList.forEach(priznItem => {
              let code = '';
              if (typeof priznItem === 'string') {
                if (signsHandbook && signsHandbook.references) {
                  const sign = signsHandbook.references.find(
                    r => r.id.toString() === String(priznItem)
                  );
                  code = sign && sign.code ? sign.code.toString() : priznItem;
                } else {
                  code = priznItem;
                }
              } else if (priznItem && priznItem.code) {
                code = priznItem.code.toString();
              } else if (priznItem && priznItem.id) {
                if (signsHandbook && signsHandbook.references) {
                  const sign = signsHandbook.references.find(
                    r => r.id.toString() === String(priznItem.id)
                  );
                  code = sign && sign.code ? sign.code.toString() : priznItem.id.toString();
                } else {
                  code = priznItem.id.toString();
                }
              }
              if (code) {
                priznCodesSet.add(code);
              }
            });
          }
          
          // Добавляем или обновляем причину в карте
          if (!causesMap.has(causeCode)) {
            causesMap.set(causeCode, {
              priznCodes: new Set(),
              resSum: 0
            });
          }
          
          const causeData = causesMap.get(causeCode);
          // Добавляем все признаки в Set (без дубликатов)
          priznCodesSet.forEach(code => {
            causeData.priznCodes.add(code);
          });
          // Суммируем результаты для повторяющихся причин
          causeData.resSum += resTotal;
        });
      });
    });
    
    // Форматируем результат
    const causesList = [];
    causesMap.forEach((causeData, causeCode) => {
      const priznStr = causeData.priznCodes.size > 0 
        ? `(${Array.from(causeData.priznCodes).join(', ')})` 
        : '';
      const formattedCause = `${causeCode}${priznStr}/${causeData.resSum.toFixed(1)}`;
      causesList.push(formattedCause);
    });
    
    return causesList.join('; ');
  };

  return (
    <div style={{ marginTop: '20px' }}>
      <h3 style={{ 
        margin: '0 0 15px 0', 
        fontSize: '16px', 
        fontWeight: '600',
        color: '#333'
      }}>
        Расчеты
      </h3>
      
      <div 
        ref={calculationsScrollRef}
        onScroll={handleCalculationsScroll}
        style={{
          overflowX: 'auto',
          border: '1px solid #dee2e6',
          borderRadius: '6px',
          backgroundColor: '#fff'
        }}>
        <table style={{
          width: '100%',
          minWidth: '1000px',
          borderCollapse: 'collapse',
          fontSize: '12px'
        }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <th style={{ 
                minWidth: '100px', 
                width: '100px',
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '600'
              }}>
                Наименование
              </th>
              <th style={{ 
                minWidth: '50px', 
                width: '50px',
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '600'
              }}>
                I (здор)
              </th>
              <th style={{ 
                minWidth: '50px', 
                width: '50px',
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '600'
              }}>
                II (ослаб)
              </th>
              <th style={{ 
                minWidth: '50px', 
                width: '50px',
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '600'
              }}>
                III (с.осл.)
              </th>
              <th style={{ 
                minWidth: '50px', 
                width: '50px',
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '600'
              }}>
                IV (усых)
              </th>
              <th style={{ 
                minWidth: '50px', 
                width: '50px',
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '600'
              }}>
                VA (св.сс.)
              </th>
              <th style={{ 
                minWidth: '50px', 
                width: '50px',
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '600'
              }}>
                VБ (св.вв.)
              </th>
              <th style={{ 
                minWidth: '50px', 
                width: '50px',
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '600'
              }}>
                VВ (св.бл)
              </th>
              <th style={{ 
                minWidth: '50px', 
                width: '50px',
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '600'
              }}>
                VГ (ст.сс)
              </th>
              <th style={{ 
                minWidth: '50px', 
                width: '50px',
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '600'
              }}>
                VД (ст.вв)
              </th>
              <th style={{ 
                minWidth: '50px', 
                width: '50px',
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '600'
              }}>
                VЕ (ст.бл)
              </th>
              <th style={{ 
                minWidth: '50px', 
                width: '50px',
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '600'
              }}>
                СКС
              </th>
              <th style={{ 
                minWidth: '75px', 
                width: '75px',
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '600'
              }}>
                Отпад общий
              </th>
              <th style={{ 
                minWidth: '75px', 
                width: '75px',
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '600'
              }}>
                Отпад текущий
              </th>
              <th style={{ 
                minWidth: '200px', 
                width: '200px',
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '600',
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                lineHeight: '1.3'
              }}>
                Доля повреждённых деревьев по причинам повреждения, % от запаса
              </th>
              <th style={{ 
                minWidth: '200px', 
                width: '200px',
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '600',
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                lineHeight: '1.3'
              }}>
                Доля повреждённых деревьев по причинам повреждения для определения очага, % от запаса
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Итог по 1-му ярусу - всегда показываем */}
            <tr>
              <td style={{ 
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'left',
                fontWeight: '500'
              }}>
                Итог по 1-му ярусу
              </td>
              <td style={{ 
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '500'
              }}>
                {renderYarusTotalCell(1, 'bpodolap')}
              </td>
              <td style={{ 
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '500'
              }}>
                {renderYarusTotalCell(1, 'osldolap')}
              </td>
              <td style={{ 
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '500'
              }}>
                {renderYarusTotalCell(1, 'sosldolap')}
              </td>
              <td style={{ 
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '500'
              }}>
                {renderYarusTotalCell(1, 'usdolap')}
              </td>
              <td style={{ 
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '500'
              }}>
                {renderYarusTotalCell(1, 'svsuhdolap')}
              </td>
              <td style={{ 
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '500'
              }}>
                {renderYarusTotalCell(1, 'svvtrdolap')}
              </td>
              <td style={{ 
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '500'
              }}>
                {renderYarusTotalCell(1, 'svburdolap')}
              </td>
              <td style={{ 
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '500'
              }}>
                {renderYarusTotalCell(1, 'stsuhdolap')}
              </td>
              <td style={{ 
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '500'
              }}>
                {renderYarusTotalCell(1, 'stvtrdolap')}
              </td>
              <td style={{ 
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '500'
              }}>
                {renderYarusTotalCell(1, 'stburdolap')}
              </td>
              <td style={{ 
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '500'
              }}>
                {renderYarusTotalCell(1, 'sksporoda')}
              </td>
              <td style={{ 
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '500'
              }}>
                {renderYarusTotalCell(1, 'ooporoda')}
              </td>
              <td style={{ 
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '500'
              }}>
                {renderYarusTotalCell(1, 'toporoda')}
              </td>
              <td style={{ 
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '500',
                fontSize: '11px',
                lineHeight: '1.3',
                whiteSpace: 'normal',
                wordWrap: 'break-word'
              }}>
                {calculateCausesForYarus(1, false)}
              </td>
              <td style={{ 
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '500',
                fontSize: '11px',
                lineHeight: '1.3',
                whiteSpace: 'normal',
                wordWrap: 'break-word'
              }}>
                {calculateCausesForYarus(1, true)}
              </td>
            </tr>

            {/* Итог по 2-му ярусу - показываем только если есть данные по 2-му ярусу */}
            {(() => {
              const secondYarusData = displayData.data.filter(item => {
                const itemYarus = String(item.yarus || '').trim();
                return itemYarus === '2' || Number(itemYarus) === 2;
              });
              const hasSecondYarus = secondYarusData.length > 0;
              
              if (hasSecondYarus) {
                return (
                  <tr>
                    <td style={{ 
                      padding: '8px',
                      border: '1px solid #dee2e6',
                      textAlign: 'left',
                      fontWeight: '500'
                    }}>
                      Итог по 2-му ярусу
                    </td>
                    <td style={{ 
                      padding: '8px',
                      border: '1px solid #dee2e6',
                      textAlign: 'center',
                      fontWeight: '500'
                    }}>
                      {renderYarusTotalCell(2, 'bpodolap')}
                    </td>
                    <td style={{ 
                      padding: '8px',
                      border: '1px solid #dee2e6',
                      textAlign: 'center',
                      fontWeight: '500'
                    }}>
                      {renderYarusTotalCell(2, 'osldolap')}
                    </td>
                    <td style={{ 
                      padding: '8px',
                      border: '1px solid #dee2e6',
                      textAlign: 'center',
                      fontWeight: '500'
                    }}>
                      {renderYarusTotalCell(2, 'sosldolap')}
                    </td>
                    <td style={{ 
                      padding: '8px',
                      border: '1px solid #dee2e6',
                      textAlign: 'center',
                      fontWeight: '500'
                    }}>
                      {renderYarusTotalCell(2, 'usdolap')}
                    </td>
                    <td style={{ 
                      padding: '8px',
                      border: '1px solid #dee2e6',
                      textAlign: 'center',
                      fontWeight: '500'
                    }}>
                      {renderYarusTotalCell(2, 'svsuhdolap')}
                    </td>
                    <td style={{ 
                      padding: '8px',
                      border: '1px solid #dee2e6',
                      textAlign: 'center',
                      fontWeight: '500'
                    }}>
                      {renderYarusTotalCell(2, 'svvtrdolap')}
                    </td>
                    <td style={{ 
                      padding: '8px',
                      border: '1px solid #dee2e6',
                      textAlign: 'center',
                      fontWeight: '500'
                    }}>
                      {renderYarusTotalCell(2, 'svburdolap')}
                    </td>
                    <td style={{ 
                      padding: '8px',
                      border: '1px solid #dee2e6',
                      textAlign: 'center',
                      fontWeight: '500'
                    }}>
                      {renderYarusTotalCell(2, 'stsuhdolap')}
                    </td>
                    <td style={{ 
                      padding: '8px',
                      border: '1px solid #dee2e6',
                      textAlign: 'center',
                      fontWeight: '500'
                    }}>
                      {renderYarusTotalCell(2, 'stvtrdolap')}
                    </td>
                    <td style={{ 
                      padding: '8px',
                      border: '1px solid #dee2e6',
                      textAlign: 'center',
                      fontWeight: '500'
                    }}>
                      {renderYarusTotalCell(2, 'stburdolap')}
                    </td>
                    <td style={{ 
                      padding: '8px',
                      border: '1px solid #dee2e6',
                      textAlign: 'center',
                      fontWeight: '500'
                    }}>
                      {renderYarusTotalCell(2, 'sksporoda')}
                    </td>
                    <td style={{ 
                      padding: '8px',
                      border: '1px solid #dee2e6',
                      textAlign: 'center',
                      fontWeight: '500'
                    }}>
                      {renderYarusTotalCell(2, 'ooporoda')}
                    </td>
                    <td style={{ 
                      padding: '8px',
                      border: '1px solid #dee2e6',
                      textAlign: 'center',
                      fontWeight: '500'
                    }}>
                      {renderYarusTotalCell(2, 'toporoda')}
                    </td>
                    <td style={{ 
                      padding: '8px',
                      border: '1px solid #dee2e6',
                      textAlign: 'center',
                      fontWeight: '500',
                      fontSize: '11px',
                      lineHeight: '1.3',
                      whiteSpace: 'normal',
                      wordWrap: 'break-word'
                    }}>
                      {calculateCausesForYarus(2, false)}
                    </td>
                    <td style={{ 
                      padding: '8px',
                      border: '1px solid #dee2e6',
                      textAlign: 'center',
                      fontWeight: '500',
                      fontSize: '11px',
                      lineHeight: '1.3',
                      whiteSpace: 'normal',
                      wordWrap: 'break-word'
                    }}>
                      {calculateCausesForYarus(2, true)}
                    </td>
                  </tr>
                );
              }
              return null;
            })()}

            {/* Итог по 3-му ярусу - показываем только если есть данные по 3-му ярусу */}
            {(() => {
              const thirdYarusData = displayData.data.filter(item => {
                const itemYarus = String(item.yarus || '').trim();
                return itemYarus === '3' || Number(itemYarus) === 3;
              });
              const hasThirdYarus = thirdYarusData.length > 0;
              
              if (hasThirdYarus) {
                return (
                  <tr>
                    <td style={{ 
                      padding: '8px',
                      border: '1px solid #dee2e6',
                      textAlign: 'left',
                      fontWeight: '500'
                    }}>
                      Итог по 3-му ярусу
                    </td>
                    <td style={{ 
                      padding: '8px',
                      border: '1px solid #dee2e6',
                      textAlign: 'center',
                      fontWeight: '500'
                    }}>
                      {renderYarusTotalCell(3, 'bpodolap')}
                    </td>
                    <td style={{ 
                      padding: '8px',
                      border: '1px solid #dee2e6',
                      textAlign: 'center',
                      fontWeight: '500'
                    }}>
                      {renderYarusTotalCell(3, 'osldolap')}
                    </td>
                    <td style={{ 
                      padding: '8px',
                      border: '1px solid #dee2e6',
                      textAlign: 'center',
                      fontWeight: '500'
                    }}>
                      {renderYarusTotalCell(3, 'sosldolap')}
                    </td>
                    <td style={{ 
                      padding: '8px',
                      border: '1px solid #dee2e6',
                      textAlign: 'center',
                      fontWeight: '500'
                    }}>
                      {renderYarusTotalCell(3, 'usdolap')}
                    </td>
                    <td style={{ 
                      padding: '8px',
                      border: '1px solid #dee2e6',
                      textAlign: 'center',
                      fontWeight: '500'
                    }}>
                      {renderYarusTotalCell(3, 'svsuhdolap')}
                    </td>
                    <td style={{ 
                      padding: '8px',
                      border: '1px solid #dee2e6',
                      textAlign: 'center',
                      fontWeight: '500'
                    }}>
                      {renderYarusTotalCell(3, 'svvtrdolap')}
                    </td>
                    <td style={{ 
                      padding: '8px',
                      border: '1px solid #dee2e6',
                      textAlign: 'center',
                      fontWeight: '500'
                    }}>
                      {renderYarusTotalCell(3, 'svburdolap')}
                    </td>
                    <td style={{ 
                      padding: '8px',
                      border: '1px solid #dee2e6',
                      textAlign: 'center',
                      fontWeight: '500'
                    }}>
                      {renderYarusTotalCell(3, 'stsuhdolap')}
                    </td>
                    <td style={{ 
                      padding: '8px',
                      border: '1px solid #dee2e6',
                      textAlign: 'center',
                      fontWeight: '500'
                    }}>
                      {renderYarusTotalCell(3, 'stvtrdolap')}
                    </td>
                    <td style={{ 
                      padding: '8px',
                      border: '1px solid #dee2e6',
                      textAlign: 'center',
                      fontWeight: '500'
                    }}>
                      {renderYarusTotalCell(3, 'stburdolap')}
                    </td>
                    <td style={{ 
                      padding: '8px',
                      border: '1px solid #dee2e6',
                      textAlign: 'center',
                      fontWeight: '500'
                    }}>
                      {renderYarusTotalCell(3, 'sksporoda')}
                    </td>
                    <td style={{ 
                      padding: '8px',
                      border: '1px solid #dee2e6',
                      textAlign: 'center',
                      fontWeight: '500'
                    }}>
                      {renderYarusTotalCell(3, 'ooporoda')}
                    </td>
                    <td style={{ 
                      padding: '8px',
                      border: '1px solid #dee2e6',
                      textAlign: 'center',
                      fontWeight: '500'
                    }}>
                      {renderYarusTotalCell(3, 'toporoda')}
                    </td>
                    <td style={{ 
                      padding: '8px',
                      border: '1px solid #dee2e6',
                      textAlign: 'center',
                      fontWeight: '500',
                      fontSize: '11px',
                      lineHeight: '1.3',
                      whiteSpace: 'normal',
                      wordWrap: 'break-word'
                    }}>
                      {calculateCausesForYarus(3, false)}
                    </td>
                    <td style={{ 
                      padding: '8px',
                      border: '1px solid #dee2e6',
                      textAlign: 'center',
                      fontWeight: '500',
                      fontSize: '11px',
                      lineHeight: '1.3',
                      whiteSpace: 'normal',
                      wordWrap: 'break-word'
                    }}>
                      {calculateCausesForYarus(3, true)}
                    </td>
                  </tr>
                );
              }
              return null;
            })()}

            {/* Итог по насаждению - всегда показываем, суммируем все данные */}
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <td style={{ 
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'left',
                fontWeight: '600',
                fontSize: '13px'
              }}>
                Итог по насаждению
              </td>
              <td style={{ 
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '600',
                fontSize: '13px'
              }}>
                {totals.bpodolap || '0.00'}
              </td>
              <td style={{ 
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '600',
                fontSize: '13px'
              }}>
                {totals.osldolap || '0.00'}
              </td>
              <td style={{ 
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '600',
                fontSize: '13px'
              }}>
                {totals.sosldolap || '0.00'}
              </td>
              <td style={{ 
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '600',
                fontSize: '13px'
              }}>
                {totals.usdolap || '0.00'}
              </td>
              <td style={{ 
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '600',
                fontSize: '13px'
              }}>
                {totals.svsuhdolap || '0.00'}
              </td>
              <td style={{ 
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '600',
                fontSize: '13px'
              }}>
                {totals.svvtrdolap || '0.00'}
              </td>
              <td style={{ 
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '600',
                fontSize: '13px'
              }}>
                {totals.svburdolap || '0.00'}
              </td>
              <td style={{ 
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '600',
                fontSize: '13px'
              }}>
                {totals.stsuhdolap || '0.00'}
              </td>
              <td style={{ 
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '600',
                fontSize: '13px'
              }}>
                {totals.stvtrdolap || '0.00'}
              </td>
              <td style={{ 
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '600',
                fontSize: '13px'
              }}>
                {totals.stburdolap || '0.00'}
              </td>
              <td style={{ 
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '600',
                fontSize: '13px'
              }}>
                {totals.sksporoda || '0.00'}
              </td>
              <td style={{ 
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '600',
                fontSize: '13px'
              }}>
                {totals.ooporoda || '0.00'}
              </td>
              <td style={{ 
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '600',
                fontSize: '13px'
              }}>
                {totals.toporoda || '0.00'}
              </td>
              <td style={{ 
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '600',
                fontSize: '11px',
                lineHeight: '1.3',
                whiteSpace: 'normal',
                wordWrap: 'break-word'
              }}>
                {calculateCausesForNasazhdeniya(false)}
              </td>
              <td style={{ 
                padding: '8px',
                border: '1px solid #dee2e6',
                textAlign: 'center',
                fontWeight: '600',
                fontSize: '11px',
                lineHeight: '1.3',
                whiteSpace: 'normal',
                wordWrap: 'break-word'
              }}>
                {calculateCausesForNasazhdeniya(true)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {showCauseSignWarning && (
        <div style={{
          marginTop: '10px',
          padding: '10px 14px',
          backgroundColor: '#fff8e1',
          border: '1px solid #ffe082',
          borderRadius: '6px',
          color: '#8a6d00',
          fontSize: '13px',
          lineHeight: '1.4'
        }}>
          Насаждение не является полностью здоровым (и (или) здоровым и ослабленным) — заполните
          причины и признаки повреждения насаждений.
        </div>
      )}

      {/* Слайдбар для прокрутки таблицы расчетов */}
      <div style={{
        marginTop: '10px',
        padding: '10px',
        backgroundColor: '#f8f9fa',
        border: '1px solid #e9ecef',
        borderRadius: '6px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}>
        <span style={{ 
          fontSize: '12px', 
          color: '#6c757d',
          minWidth: '60px'
        }}>
          Прокрутка:
        </span>
        <input
          type="range"
          min="0"
          max="100"
          value={calculationsScrollPosition}
          onChange={handleCalculationsSliderChange}
          className="table-scroll-slider"
          style={{
            flex: 1
          }}
        />
        <span style={{ 
          fontSize: '12px', 
          color: '#6c757d',
          minWidth: '30px',
          textAlign: 'right'
        }}>
          {Math.round(calculationsScrollPosition)}%
        </span>
      </div>
    </div>
  );
};

export default CalculationsSection;

